import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error inside ErrorBoundary:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen bg-cv-page text-cv-primary flex flex-col items-center justify-center p-6 font-sans">
          <div className="max-w-md w-full bg-cv-panel border border-cv-danger/30 rounded-2xl p-8 text-center shadow-2xl">
            <div className="w-16 h-16 bg-red-600/10 text-cv-danger rounded-full flex items-center justify-center mx-auto mb-4 border border-cv-danger/50">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-xl font-black text-cv-primary uppercase tracking-tight mb-2">Something went wrong</h1>
            <p className="text-xs text-cv-muted font-mono mb-6 leading-relaxed">
              An unexpected error occurred in the application shell.
            </p>
            <div className="bg-cv-page border border-cv-border rounded-lg p-3 text-[10px] font-mono text-left text-cv-danger overflow-x-auto max-h-[150px] mb-6 whitespace-pre-wrap select-text">
              {this.state.error?.toString()}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page text-xs font-black uppercase rounded-lg transition-all tracking-wider shadow-lg shadow-black/30"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
